import type { Store, UndoState } from '@wiztime/storage';

/** An immutable view of the undo history; `version` increases on every change. */
export interface UndoSnapshot {
  readonly state: UndoState;
  readonly version: number;
}

/**
 * `Store.getUndoState()` builds a new object on every call, so it cannot feed
 * `useSyncExternalStore` directly. This model caches the latest state as a stable snapshot and
 * counts changes (toast "undo" buttons are valid only while the version is unchanged).
 */
export interface UndoModel {
  subscribe(listener: () => void): () => void;
  getSnapshot(): UndoSnapshot;
}

export function createUndoModel(store: Store): UndoModel {
  let snapshot: UndoSnapshot = { state: store.getUndoState(), version: 0 };
  const listeners = new Set<() => void>();
  store.subscribe((state) => {
    snapshot = { state, version: snapshot.version + 1 };
    listeners.forEach((listener) => listener());
  });
  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => snapshot,
  };
}
